// FilterSummary（Sprint 4.3.2）：候选过滤三态汇总 + 已过滤清单
// - 三态：ACCEPT（生成气泡）/ REVIEW（待人工复核）/ 已过滤（REJECT，来自 dropped，不进气泡）
// - 点击"查看已过滤"展开 dropped 中 candidate-filter 命中的明细（含文本 + 原因 + 位置）
// 原则：宁少一个气泡等人工审核，也不要图框/表格/序号上生成专业错误气泡。
import { useState } from 'react';
import type { Feature, DrawingDetail } from '../types';

interface Props {
  features: Feature[];
  dropped: DrawingDetail['dropped'];
}

// dropped 项 reason 前缀为 candidate-filter:*
const CF_PREFIX = 'candidate-filter:';
const filterReasonCn: Record<string, string> = {
  ZONE_NUMBER: '边缘区号',
  TABLE_VALUE: '表格值',
  BALLOON_NUMBER: '已有气泡编号',
  DATUM_LABEL: '基准标签',
  NOTE_TEXT: '注释文字',
  TITLE_BLOCK: '标题栏',
  REVISION: '修订',
  PART_NUMBER: '料号/文件号',
  STD_NOTE: '标准注释',
  NON_DIMENSION_TEXT: '非尺寸文字',
  LOW_CONFIDENCE: '低置信',
  GEOMETRY_CONTEXT: '几何上下文',
  GRID_LABEL: '区域号(网格标签)',
  // P0-1（2026-10-03）：邻域语义否定三兄弟
  COORD_TABLE: '测量点坐标值',
  STANDARD_REF: '标准号段',
  GDT_FRAME_VALUE: '公差框内的公差值',
  OTHER: '其他',
  OTHER_REVIEW: '待复核',
  ANCHOR_INVALID: '锚点无效',
};

function cn(reason?: string): string {
  if (!reason) return '其他';
  // dropped 的 reason 可能是 'candidate-filter:TABLE_VALUE'
  const code = reason.startsWith(CF_PREFIX) ? reason.slice(CF_PREFIX.length) : reason;
  const bare = code.split(':').pop() || code;
  return filterReasonCn[bare] ? `${filterReasonCn[bare]} (${bare})` : reason;
}

export function FilterSummary({ features, dropped }: Props) {
  const [showList, setShowList] = useState(false);
  // 三态统计
  let accept = 0, review = 0;
  for (const f of features) {
    const fs = (f.filter_status ?? 'ACCEPT');
    if (fs === 'REVIEW') review++; else accept++;
  }
  const cfDropped = (dropped ?? []).filter(d =>
    String(d?.reason ?? '').startsWith(CF_PREFIX) || String(d?.stage ?? '') === 'candidate_filter');

  return (
    <div className="filter-summary">
      <div className="fs-badges">
        <span className="fs-badge accept" title="候选过滤 ACCEPT：正常生成气泡">✓ 识别 {accept}</span>
        {review > 0 && (
          <span className="fs-badge review" title="候选过滤 REVIEW：需人工确认是否生成气泡（界面以黄色虚线气泡标示）">⚠ 待复核 {review}</span>
        )}
        {cfDropped.length > 0 && (
          <button className="fs-badge reject" title="候选过滤 REJECT：不进气泡，已从图框/表格/序号移除"
                  onClick={() => setShowList(v => !v)}>
            已过滤 {cfDropped.length} {showList ? '▴' : '▾'}
          </button>
        )}
      </div>
      {showList && cfDropped.length > 0 && (
        <div className="fs-list">
          <div className="fs-list-head">候选过滤已移除（REJECT · 不进气泡，避免图框/表格/序号误标）</div>
          <table className="fs-table">
            <thead><tr><th>文本</th><th>原因</th><th>位置 x,y</th></tr></thead>
            <tbody>
              {cfDropped.map((d, i) => (
                <tr key={i}>
                  <td className="mono">{String(d?.text ?? '').slice(0, 32)}</td>
                  <td>{cn(d?.reason)}</td>
                  <td className="mono">
                    {d?.bbox ? `${Math.round(d.bbox[0])},${Math.round(d.bbox[1])}` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
